import { Check } from 'lucide-react'
import { NODE_TYPES, EDGE_TYPES, ENTITY_SUBTYPE_STYLES, INVENTION_NODE_TYPES, INVENTION_EDGE_TYPES } from '../../constants/nodeTypes'

// #4612/#4646: default scope = today's invention-graph keys, captured as a
// stable module-level constant (not recomputed per-render) so existing
// callers (InlineConnectionsPanel — Project/Document Connections — pass no
// scoping props) see EXACTLY today's rows, unaffected by PATENT_NODE_TYPES/
// PATENT_EDGE_TYPES being added to the shared NODE_TYPES/EDGE_TYPES maps.
const DEFAULT_NODE_TYPE_KEYS = Object.keys(INVENTION_NODE_TYPES)
const DEFAULT_EDGE_TYPE_KEYS = Object.keys(INVENTION_EDGE_TYPES)

interface ConnectionsLegendProps {
  hiddenNodeTypes?: Set<string>
  hiddenEdgeTypes?: Set<string>
  onToggleNodeType?: (type: string) => void
  onToggleEdgeType?: (type: string) => void
  /** #4612/#4646: restrict the legend to these node/edge keys (e.g. the
   * Patent Connections panel passes Object.keys(PATENT_NODE_TYPES)). Omit to
   * get today's invention-graph keys — the exact prior behaviour. */
  nodeTypeKeys?: string[]
  edgeTypeKeys?: string[]
}

/** A single legend row: swatch + label + an explicit shown/hidden checkbox.
 * #4612/#4646: replaces the old bare hover-highlight (too subtle to read as
 * "this is a clickable toggle") with a visible checked/unchecked box that
 * mirrors the row's actual state at a glance, for both this graph and the
 * existing Project/Document Connections legend. */
function LegendRow({ shape, color, label, hidden, interactive, onClick }: {
  shape: 'square' | 'diamond'
  color: string
  label: string
  hidden: boolean
  interactive: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={!hidden}
      title={interactive ? (hidden ? `Show ${label}` : `Hide ${label}`) : undefined}
      className={`flex items-center gap-1.5 w-full text-left rounded px-1 py-1 transition-colors ${
        interactive ? 'hover:bg-iflow-50 active:bg-iflow-100 cursor-pointer' : 'cursor-default'
      }`}
    >
      <span
        className={`flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 rounded-sm border transition-colors ${
          hidden ? 'border-gray-300 bg-white' : 'border-iflow-500 bg-iflow-500'
        }`}
      >
        {!hidden && <Check size={10} strokeWidth={3} className="text-white" />}
      </span>
      <div
        className={`w-2.5 h-2.5 flex-shrink-0 transition-opacity ${shape === 'diamond' ? 'rotate-45' : 'rounded-sm'} ${hidden ? 'opacity-35' : ''}`}
        style={{ backgroundColor: hidden ? '#D1D5DB' : color }}
      />
      <span className={`text-xs whitespace-nowrap transition-opacity ${hidden ? 'text-gray-500 line-through' : 'text-gray-600'}`}>
        {label}
      </span>
    </button>
  )
}

export default function ConnectionsLegend({
  hiddenNodeTypes,
  hiddenEdgeTypes,
  onToggleNodeType,
  onToggleEdgeType,
  nodeTypeKeys = DEFAULT_NODE_TYPE_KEYS,
  edgeTypeKeys = DEFAULT_EDGE_TYPE_KEYS,
}: ConnectionsLegendProps) {
  const interactive = !!onToggleNodeType
  const showEntitySubtypes = nodeTypeKeys.includes('entity')

  return (
    <div className="flex flex-col gap-1 overflow-y-auto py-1">
      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Nodes</span>
      {Object.entries(NODE_TYPES)
        .filter(([key]) => key !== 'entity' && nodeTypeKeys.includes(key))
        .map(([key, config]) => {
          const hidden = !!hiddenNodeTypes?.has(key)
          return (
            <LegendRow
              key={key}
              shape={config.shape === 'diamond' ? 'diamond' : 'square'}
              color={config.color}
              label={config.label}
              hidden={hidden}
              interactive={interactive}
              onClick={() => onToggleNodeType?.(key)}
            />
          )
        })}
      {/* #4612/#4646: entity-subtype rows are gated on 'entity' being in
          nodeTypeKeys — the patent panel's key list omits 'entity' entirely,
          so Material/Method/Application never render for a patent graph
          (Red-Team Round 2 fix: this loop had no gating at all before). */}
      {showEntitySubtypes && Object.entries(ENTITY_SUBTYPE_STYLES).map(([key, config]) => {
        const entityKey = `entity:${key}`
        const hidden = !!hiddenNodeTypes?.has(entityKey)
        return (
          <LegendRow
            key={`entity-${key}`}
            shape="diamond"
            color={config.color}
            label={config.label}
            hidden={hidden}
            interactive={interactive}
            onClick={() => onToggleNodeType?.(entityKey)}
          />
        )
      })}

      <div className="h-px w-full bg-gray-200 my-1" />

      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Edges</span>
      {Object.entries(EDGE_TYPES)
        .filter(([key]) => edgeTypeKeys.includes(key))
        .map(([key, config]) => {
          const hidden = hiddenEdgeTypes?.has(key)
          return (
            <button
              key={key}
              onClick={() => onToggleEdgeType?.(key)}
              aria-pressed={!hidden}
              title={interactive ? (hidden ? `Show ${config.label}` : `Hide ${config.label}`) : undefined}
              className={`flex items-center gap-1.5 w-full text-left rounded px-1 py-1 transition-colors ${
                interactive ? 'hover:bg-iflow-50 active:bg-iflow-100 cursor-pointer' : 'cursor-default'
              }`}
            >
              <span
                className={`flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 rounded-sm border transition-colors ${
                  hidden ? 'border-gray-300 bg-white' : 'border-iflow-500 bg-iflow-500'
                }`}
              >
                {!hidden && <Check size={10} strokeWidth={3} className="text-white" />}
              </span>
              <div
                className="w-3 h-0.5 flex-shrink-0 transition-opacity"
                style={{
                  backgroundColor: hidden ? '#D1D5DB' : config.color,
                  borderTop: config.dashed ? '1px dashed' : undefined,
                }}
              />
              <span className={`text-xs whitespace-nowrap transition-opacity ${hidden ? 'text-gray-500 line-through' : 'text-gray-600'}`}>
                {config.label}
              </span>
            </button>
          )
        })}
    </div>
  )
}
